import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import {
  MapPin,
  Navigation,
  Plus,
  X,
  RefreshCw,
  Cctv,
  PersonStanding,
  Car,
  Bike,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  pinService,
  isDirectional,
  rotatesBody,
  type PinData,
  type MultilingualText,
  type PinKind,
} from '@/services/pinService'
import {
  GRIP_ARROW_INNER,
  HORSE_PATHS,
  HORSE_VIEWBOX,
  PIN_ICON_SVG,
} from '@/services/pinMarker'
import { CameraOverlayManager } from '@/services/cameraOverlay'
import { AlertZoneOverlay } from '@/services/alertZoneOverlay'
import { loadAlertZones, saveAlertZones } from '@/services/pushService'
import {
  MAX_ALERT_ZONES,
  sanitizeZones,
  type AlertZone,
} from '@/services/alertZones'
import { NotificationToggle } from '@/components/NotificationToggle'

// Horse-and-rider silhouette (from the supplied horse.svg, Illustrator export)
// on a 256x256 grid, facing east like the other rotating icons. Kept as data so
// the marker and picker button render from one source. Filled, unlike the
// stroked vehicle icons. Artwork lives in services/pinMarker so server-side
// map images (mapshot) draw the exact same markers.

// Horse glyph for the kind picker button; `currentColor` picks up the white
// button text, matching the lucide icons beside it.
const HorseIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox={`0 0 ${HORSE_VIEWBOX} ${HORSE_VIEWBOX}`}
    fill="currentColor"
    stroke="none"
    className={className}
    aria-hidden="true"
  >
    {HORSE_PATHS.map(({ id, d }) => (
      <path key={id} d={d} />
    ))}
  </svg>
)

/** Heading a directional pin points in when it is first dropped. */
const DEFAULT_HEADING = 90 // east, matching how the car/bike icons are drawn

// Drag handle orbiting a car/velo pin. It shows an arrow when the vehicle is
// moving (tap to park, drag to steer) and a hollow dot when parked (tap to set
// it moving). `.heading-orbit` is a zero-sized point at the marker's centre, so
// rotating it swings the arrow around the vehicle.
const headingHandleHTML = (interactive: boolean) => `
          <div class="heading-orbit absolute left-1/2 top-1/2 w-0 h-0">
            <div class="heading-grip absolute flex items-center justify-center" style="left:-14px;top:-42px;width:28px;height:28px;${
              interactive
                ? 'touch-action:none;cursor:pointer'
                : 'pointer-events:none'
            }">
              <svg class="grip-arrow" width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                ${GRIP_ARROW_INNER}
              </svg>
              <svg class="grip-dot" width="20" height="20" viewBox="0 0 24 24" style="display:none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="6" fill="#ffffff" stroke="#000000" stroke-width="2.5"/>
              </svg>
            </div>
          </div>`

// Creation time under the marker. Positioned absolutely so it doesn't grow the
// element, which would shift the icon off the pin's coordinates. Styled with a
// white halo rather than a background, to match the bare black icons.
const pinTimeHTML = (label: string) =>
  `<div class="pin-time" style="position:absolute;left:50%;top:100%;transform:translateX(-50%);margin-top:1px;white-space:nowrap;font-size:11px;line-height:1;font-weight:600;color:#000000;text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 2px #fff;pointer-events:none">${label}</div>`

const pinHTML = (
  kind: PinKind,
  timeLabel = '',
  owned = true
) => `<div class="pin-inner cursor-pointer relative">
          <svg class="pin-body" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">
            ${PIN_ICON_SVG[kind]}
          </svg>
${isDirectional(kind) ? headingHandleHTML(owned) : ''}
${timeLabel ? pinTimeHTML(timeLabel) : ''}
        </div>`

/**
 * Orients a car/velo pin. When moving, the arrow orbits by the full heading and
 * the body is offset by 90° (the icons are drawn facing east) and mirrored once
 * past due south so it never drives on its roof. When parked (`heading` null)
 * the body sits upright and the arrow is replaced by a hollow dot — shown only
 * to the owner, since only they can tap it back into motion.
 */
const applyHeadingTransforms = (
  pinElement: HTMLElement,
  heading: number | null,
  bearing: number,
  owned: boolean,
  kind: PinKind
) => {
  const parked = heading == null
  const orbit = pinElement.querySelector<HTMLElement>('.heading-orbit')
  const body = pinElement.querySelector<HTMLElement>('.pin-body')
  const arrow = pinElement.querySelector<HTMLElement>('.grip-arrow')
  const dot = pinElement.querySelector<HTMLElement>('.grip-dot')

  if (arrow) arrow.style.display = parked ? 'none' : ''
  // The parked affordance is only actionable by the owner
  if (dot) dot.style.display = parked && owned ? '' : 'none'

  if (parked) {
    if (orbit) orbit.style.transform = 'rotate(0deg)'
    if (body) body.style.transform = ''
    return
  }

  const screenAngle = (((heading - bearing) % 360) + 360) % 360
  if (orbit) orbit.style.transform = `rotate(${screenAngle}deg)`
  if (body) {
    // Only side-view vehicles turn; a pedestrian stays upright.
    if (rotatesBody(kind)) {
      const upsideDown = screenAngle > 180 && screenAngle < 360
      body.style.transform = `rotate(${screenAngle - 90}deg)${
        upsideDown ? ' scaleY(-1)' : ''
      }`
    } else {
      body.style.transform = ''
    }
  }
}

interface PinPoint {
  id: string
  lng: number
  lat: number
  description: string // Deprecated: for backward compatibility
  descriptions?: MultilingualText // New multilingual descriptions
  createdAt?: string // Creation timestamp
  kind: PinKind
  heading: number | null // Bearing for car/velo; null = parked
  owned: boolean // Created by this browser, so editable here
  marker: maplibregl.Marker
}

interface MapComponentProps {
  className?: string
}

export function MapComponent({ className = 'w-full' }: MapComponentProps) {
  const { t, i18n } = useTranslation()

  // Only generic en/fr/ar bundles exist, so a detected regional tag such as
  // `ar-TN` still renders an Arabic interface. Everything keyed off the
  // language has to use the same base tag, or it drifts out of step with the
  // visible UI — `ar-TN` formats numbers with Latin digits, and looking up
  // `descriptions['ar-TN']` misses and falls back to English.
  const language = (i18n.language || 'en').split('-')[0]

  // Helper function to get localized description
  const getLocalizedDescription = useCallback(
    (pin: PinPoint): string => {
      if (pin.descriptions) {
        const currentLang = language as keyof MultilingualText
        return (
          pin.descriptions[currentLang] ||
          pin.descriptions.en ||
          pin.description
        )
      }
      return pin.description
    },
    [language]
  )

  // Helper function to format creation time
  const formatCreationTime = useCallback(
    (createdAt?: string): string => {
      if (!createdAt) return ''
      try {
        const date = new Date(createdAt)
        return date.toLocaleString(language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch {
        return ''
      }
    },
    [language]
  )

  // Just the clock time, for the label drawn under each marker
  const formatCreationClock = useCallback(
    (createdAt?: string): string => {
      if (!createdAt) return ''
      try {
        return new Date(createdAt).toLocaleTimeString(language, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // keeps the label narrow; "04:32 PM" is too wide here
        })
      } catch {
        return ''
      }
    },
    [language]
  )

  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<maplibregl.Map | null>(null)
  const userLocationMarker = useRef<maplibregl.Marker | null>(null)
  const pinPointsRef = useRef<PinPoint[]>([])
  const cameraOverlayRef = useRef<CameraOverlayManager | null>(null)
  const alertZoneOverlayRef = useRef<AlertZoneOverlay | null>(null)
  // Polling refreshes the map from the interval, which can't see the latest
  // render's closures — these refs bridge that gap.
  const loadPinsRef = useRef<(showLoading?: boolean) => Promise<void>>(() =>
    Promise.resolve()
  )
  // True while the user is mid-interaction, so a background refresh doesn't rip
  // markers (or a drag) out from under them.
  const busyRef = useRef(false)
  const draggingRef = useRef(false)
  const [isLoadingCameras, setIsLoadingCameras] = useState(false)
  // The map is initialized once, so keep a live ref to `t` for callbacks
  // created at init time (otherwise they'd capture the first language).
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])
  const [isLocating, setIsLocating] = useState(false)
  const [locationPermission, setLocationPermission] = useState<
    'granted' | 'denied' | 'prompt'
  >('prompt')
  const [hasUserLocation, setHasUserLocation] = useState(false)
  const [pinPoints, setPinPoints] = useState<PinPoint[]>([])
  const [isAddingPin, setIsAddingPin] = useState(false)
  const [showKindPicker, setShowKindPicker] = useState(false)
  const [pendingKind, setPendingKind] = useState<PinKind>('foot')
  const [showDescriptionModal, setShowDescriptionModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [pendingPinLocation, setPendingPinLocation] = useState<{
    lng: number
    lat: number
  } | null>(null)
  const [pinToDelete, setPinToDelete] = useState<string | null>(null)
  const [pinDescription, setPinDescription] = useState('')
  const [isEditingPin, setIsEditingPin] = useState(false)
  const [isPinReadOnly, setIsPinReadOnly] = useState(false)
  const [pinToEdit, setPinToEdit] = useState<string | null>(null)
  const [isReloading, setIsReloading] = useState(false)
  const [isSavingPin, setIsSavingPin] = useState(false)
  const [isUpdatingPin, setIsUpdatingPin] = useState(false)
  // Areas the user wants to be alerted about, and the rectangle being dragged
  // out right now (in screen pixels, relative to the drawing surface).
  const [alertZones, setAlertZones] = useState<AlertZone[]>(() =>
    loadAlertZones()
  )
  const [isDrawingZone, setIsDrawingZone] = useState(false)
  const [zoneDraft, setZoneDraft] = useState<{
    x0: number
    y0: number
    x1: number
    y1: number
  } | null>(null)
  // The overlay is only created once the map style has loaded, which happens
  // after the first render — it reads the zones from here at that point.
  const alertZonesRef = useRef<AlertZone[]>(alertZones)

  // Keep ref in sync with state
  useEffect(() => {
    pinPointsRef.current = pinPoints
  }, [pinPoints])

  useEffect(() => {
    alertZonesRef.current = alertZones
    alertZoneOverlayRef.current?.setZones(alertZones)
  }, [alertZones])

  // Runs after every render (no dep array) so the polling interval always sees
  // the latest reload function and interaction state.
  useEffect(() => {
    loadPinsRef.current = loadPinsFromBackend
    busyRef.current =
      isSavingPin ||
      isUpdatingPin ||
      isAddingPin ||
      showKindPicker ||
      isDrawingZone ||
      showDescriptionModal ||
      showDeleteConfirm
  })

  // Update pin tooltips and time labels when language changes
  useEffect(() => {
    pinPoints.forEach((pin) => {
      const localizedDescription = getLocalizedDescription(pin)
      const creationTime = formatCreationTime(pin.createdAt)
      const tooltip = creationTime
        ? `${localizedDescription}\n${t('map.createdAt', 'Created')}: ${creationTime}`
        : localizedDescription
      const element = pin.marker.getElement()
      element.title = tooltip
      // Locales differ in clock format and digits (Arabic especially)
      const timeLabel = element.querySelector<HTMLElement>('.pin-time')
      if (timeLabel) {
        timeLabel.textContent = formatCreationClock(pin.createdAt)
      }
    })
  }, [
    language,
    pinPoints,
    getLocalizedDescription,
    formatCreationTime,
    formatCreationClock,
    t,
  ])

  // Update textarea content when language changes while editing a pin
  useEffect(() => {
    if (isEditingPin && pinToEdit && showDescriptionModal) {
      const currentPin = pinPointsRef.current.find((p) => p.id === pinToEdit)
      if (currentPin) {
        const localizedDesc = getLocalizedDescription(currentPin)
        setPinDescription(localizedDesc)
      }
    }
  }, [
    language,
    isEditingPin,
    pinToEdit,
    showDescriptionModal,
    getLocalizedDescription,
  ])

  const addPinPoint = async (
    lng: number,
    lat: number,
    description: string,
    kind: PinKind
  ) => {
    if (!map.current || isSavingPin) return

    const id = `pin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const currentLang = language as 'en' | 'fr' | 'ar'

    try {
      setIsSavingPin(true)

      // Save to backend - translation will happen on the backend
      await pinService.addPin(
        {
          id,
          lng,
          lat,
          description,
          kind,
          // Vehicles start parked; the user taps the dot to set them moving
          ...(isDirectional(kind) ? { heading: null } : {}),
        },
        currentLang
      )

      // Reload pins from backend to get translated versions and avoid duplicates
      await loadPinsFromBackend()
    } catch (error) {
      console.error('Failed to add pin:', error)
      alert(t('map.errorAddingPin') || 'Failed to add pin. Please try again.')
    } finally {
      setIsSavingPin(false)
    }
  }

  const removePinPoint = async (id: string) => {
    try {
      // Remove from backend first
      await pinService.deletePin(id)

      // Remove from local state and map
      setPinPoints((prev) => {
        const pinToRemove = prev.find((pin) => pin.id === id)
        if (pinToRemove) {
          pinToRemove.marker.remove()
        }
        return prev.filter((pin) => pin.id !== id)
      })
    } catch (error) {
      console.error('Failed to delete pin:', error)
      alert(
        t('map.errorDeletingPin') || 'Failed to delete pin. Please try again.'
      )
    }
  }

  /**
   * Wires the direction control on a car/velo pin: tap to toggle parked vs
   * moving, drag to steer. Heading is stored as a compass bearing (null when
   * parked) but drawn relative to the current map bearing.
   */
  const wireHeadingHandle = (
    pinElement: HTMLElement,
    pinId: string,
    initialHeading: number | null,
    kind: PinKind
  ) => {
    const grip = pinElement.querySelector<HTMLElement>('.heading-grip')
    if (!grip) return

    const TAP_SLOP_PX = 6 // movement under this counts as a tap, not a drag
    let heading = initialHeading
    // Where the arrow springs back to when tapping a parked vehicle into motion
    let lastMovingHeading = initialHeading ?? DEFAULT_HEADING
    let downPos: { x: number; y: number } | null = null
    let didRotate = false

    // Marker dragging and the map's click-to-place are both bound at the map
    // level, so swallowing these here keeps the grip from moving the pin or
    // opening the description editor.
    const swallow = (e: Event) => e.stopPropagation()
    grip.addEventListener('mousedown', swallow)
    grip.addEventListener('touchstart', swallow, { passive: true })
    grip.addEventListener('click', swallow)

    grip.addEventListener('pointerdown', (e) => {
      e.stopPropagation()
      e.preventDefault()
      downPos = { x: e.clientX, y: e.clientY }
      didRotate = false
      grip.setPointerCapture(e.pointerId)
    })

    grip.addEventListener('pointermove', (e) => {
      if (!downPos) return
      e.stopPropagation()
      if (
        !didRotate &&
        Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y) < TAP_SLOP_PX
      ) {
        return // still within tap tolerance; don't start steering yet
      }
      didRotate = true
      const rect = pinElement.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      // Screen angle, clockwise from straight up.
      const angle =
        (Math.atan2(e.clientX - centerX, centerY - e.clientY) * 180) / Math.PI
      const bearing = map.current?.getBearing() ?? 0
      heading = (angle + bearing + 360) % 360
      lastMovingHeading = heading
      applyHeadingTransforms(pinElement, heading, bearing, true, kind)
    })

    const commit = async (next: number | null) => {
      heading = next
      applyHeadingTransforms(
        pinElement,
        heading,
        map.current?.getBearing() ?? 0,
        true,
        kind
      )
      try {
        await pinService.updatePin(pinId, { heading })
        setPinPoints((prev) =>
          prev.map((pin) => (pin.id === pinId ? { ...pin, heading } : pin))
        )
      } catch (error) {
        console.error('Failed to save heading:', error)
      }
    }

    const onPointerUp = (e: PointerEvent) => {
      if (!downPos) return
      downPos = null
      grip.releasePointerCapture(e.pointerId)
      if (didRotate) {
        // A steer already updated the DOM; just persist the final heading.
        commit(heading)
      } else {
        // A tap toggles between parked and moving.
        commit(heading == null ? lastMovingHeading : null)
      }
    }
    grip.addEventListener('pointerup', onPointerUp)
    grip.addEventListener('pointercancel', onPointerUp)
  }

  /**
   * Pins only move after a deliberate hold, so brushing one while panning the
   * map can't drag it. Touch needs a long press; a mouse just needs a small
   * movement, which is the usual desktop expectation.
   */
  const wireLongPressDrag = (
    pinElement: HTMLElement,
    marker: maplibregl.Marker,
    pinId: string
  ) => {
    const LONG_PRESS_MS = 400
    const TOUCH_CANCEL_PX = 8 // moving further than this is a pan, not a hold
    const MOUSE_DRAG_PX = 4

    let activePointer: number | null = null
    let pointerType = ''
    let downPos: { x: number; y: number } | null = null
    let pressTimer: ReturnType<typeof setTimeout> | null = null
    let isDragging = false
    let suppressClick = false
    let grabOffset = { x: 0, y: 0 }
    let dragStart: maplibregl.LngLat | null = null

    const beginDrag = (e: PointerEvent) => {
      pressTimer = null
      isDragging = true
      draggingRef.current = true // pause background refresh while dragging
      dragStart = marker.getLngLat()
      pinElement.classList.add('pin-drag-ready')
      navigator.vibrate?.(30)
      map.current?.dragPan.disable()
      map.current?.touchZoomRotate.disable()

      // Remember where inside the pin the finger landed, so it doesn't jump.
      const rect = map.current?.getCanvasContainer().getBoundingClientRect()
      const markerPx = map.current?.project(marker.getLngLat())
      if (rect && markerPx) {
        grabOffset = {
          x: e.clientX - rect.left - markerPx.x,
          y: e.clientY - rect.top - markerPx.y,
        }
      }
      try {
        pinElement.setPointerCapture(e.pointerId)
      } catch {
        // Pointer capture is best-effort; dragging still works without it.
      }
    }

    const endDrag = () => {
      if (pressTimer) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
      if (isDragging) {
        map.current?.dragPan.enable()
        map.current?.touchZoomRotate.enable()
        pinElement.classList.remove('pin-drag-ready')
      }
      activePointer = null
      downPos = null
      isDragging = false
      draggingRef.current = false
    }

    pinElement.addEventListener('pointerdown', (e) => {
      if (activePointer !== null) return
      activePointer = e.pointerId
      pointerType = e.pointerType
      downPos = { x: e.clientX, y: e.clientY }
      suppressClick = false
      if (e.pointerType !== 'mouse') {
        pressTimer = setTimeout(() => beginDrag(e), LONG_PRESS_MS)
      }
    })

    pinElement.addEventListener('pointermove', (e) => {
      if (e.pointerId !== activePointer) return

      if (!isDragging) {
        if (!downPos) return
        const distance = Math.hypot(
          e.clientX - downPos.x,
          e.clientY - downPos.y
        )
        if (pointerType === 'mouse') {
          if (distance > MOUSE_DRAG_PX) beginDrag(e)
        } else if (distance > TOUCH_CANCEL_PX) {
          endDrag() // they meant to pan the map, so let it through
        }
        return
      }

      e.preventDefault()
      suppressClick = true
      const rect = map.current?.getCanvasContainer().getBoundingClientRect()
      if (!rect || !map.current) return
      marker.setLngLat(
        map.current.unproject([
          e.clientX - rect.left - grabOffset.x,
          e.clientY - rect.top - grabOffset.y,
        ])
      )
    })

    const onPointerUp = async (e: PointerEvent) => {
      if (e.pointerId !== activePointer) return
      const wasDragging = isDragging
      endDrag()
      if (!wasDragging) return

      const lngLat = marker.getLngLat()
      try {
        await pinService.updatePin(pinId, { lng: lngLat.lng, lat: lngLat.lat })
        setPinPoints((prev) =>
          prev.map((pin) =>
            pin.id === pinId
              ? { ...pin, lng: lngLat.lng, lat: lngLat.lat }
              : pin
          )
        )
      } catch (error) {
        console.error('Failed to update pin position:', error)
        if (dragStart) marker.setLngLat(dragStart)
      }
    }
    pinElement.addEventListener('pointerup', onPointerUp)
    pinElement.addEventListener('pointercancel', onPointerUp)

    // Captured so it runs before the inner click handler that opens the editor.
    pinElement.addEventListener(
      'click',
      (e) => {
        if (!suppressClick) return
        suppressClick = false
        e.stopPropagation()
      },
      true
    )
  }

  const loadPinsFromBackend = async (showLoading = false) => {
    if (!map.current) return

    try {
      if (showLoading) {
        setIsReloading(true)
      }

      // Clean up existing markers first
      pinPoints.forEach((pin) => {
        pin.marker.remove()
      })

      const savedPins = await pinService.loadPins()

      const loadedPins: PinPoint[] = []

      for (const pinData of savedPins) {
        // Create custom pin element
        const pinElement = document.createElement('div')
        pinElement.className = 'custom-pin'
        const localizedDescription = pinData.descriptions
          ? pinData.descriptions[language as keyof MultilingualText] ||
            pinData.descriptions.en ||
            pinData.description
          : pinData.description
        const creationTime = formatCreationTime(pinData.createdAt)
        const tooltip = creationTime
          ? `${localizedDescription}\n${t('map.createdAt', 'Created')}: ${creationTime}`
          : localizedDescription
        pinElement.title = tooltip
        // Pins predating the kind field render as the on-foot marker
        const kind: PinKind = pinData.kind ?? 'foot'
        // null/absent heading means parked (the default for new vehicles)
        const heading = pinData.heading ?? null
        // Pins from other visitors can be read but not changed
        const owned = pinData.owned === true
        pinElement.innerHTML = pinHTML(
          kind,
          formatCreationClock(pinData.createdAt),
          owned
        )
        if (isDirectional(kind)) {
          applyHeadingTransforms(
            pinElement,
            heading,
            map.current?.getBearing() ?? 0,
            owned,
            kind
          )
          if (owned) {
            wireHeadingHandle(pinElement, pinData.id, heading, kind)
          }
        }

        // Dragging is driven by our own long-press handler, not MapLibre's
        const marker = new maplibregl.Marker({
          element: pinElement,
          draggable: false,
        })
          .setLngLat([pinData.lng, pinData.lat])
          .addTo(map.current)

        if (owned) {
          wireLongPressDrag(pinElement, marker, pinData.id)
        }

        // Add click handler for editing description
        const pinMainElement = pinElement.querySelector('div')
        if (pinMainElement) {
          pinMainElement.addEventListener('click', (e) => {
            e.stopPropagation()
            setPinToEdit(pinData.id)
            setIsPinReadOnly(!owned)
            // Find the current pin data and use localized description
            const currentPin = pinPointsRef.current.find(
              (p) => p.id === pinData.id
            )
            if (currentPin) {
              const localizedDesc = getLocalizedDescription(currentPin)
              setPinDescription(localizedDesc)
            } else {
              setPinDescription(localizedDescription)
            }
            setIsEditingPin(true)
            setShowDescriptionModal(true)
          })
        }

        loadedPins.push({
          id: pinData.id,
          lng: pinData.lng,
          lat: pinData.lat,
          description: pinData.description || '',
          descriptions: pinData.descriptions,
          createdAt: pinData.createdAt,
          kind,
          heading,
          owned,
          marker,
        })
      }

      setPinPoints(loadedPins)
    } catch (error) {
      console.error('Failed to load pins:', error)
      // Don't show error to user as backend might not be available
    } finally {
      if (showLoading) {
        setIsReloading(false)
      }
    }
  }

  // Initialize map
  useEffect(() => {
    // Initialize map only once
    if (!map.current && mapContainer.current) {
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: '/api/tiles/styles/osm-bright/style.json',
        center: [-0.5786513880943005, 44.83798621270503], // Bordeaux coordinates
        zoom: 15,
        attributionControl: false,
        // OSM-style URL anchor (#map=zoom/lat/lng): position links — like the
        // ones sent to the Signal group — open the map right on the marker,
        // and the URL tracks the view so any moment is shareable.
        hash: 'map',
        // The style has to name our origin in full (MapLibre rejects a relative
        // sprite, and tile URLs get resolved inside a blob: worker), which the
        // server takes from APP_BASE_URL. Left unset, it can only fall back to
        // the scheme the request arrived on — plain http behind a TLS-
        // terminating proxy — and an https page then blocks every tile as mixed
        // content. The browser is the one party that knows the answer for
        // certain, so put our own URLs back on its scheme.
        transformRequest: (url) => {
          try {
            const target = new URL(url, window.location.href)
            if (
              target.host === window.location.host &&
              target.protocol !== window.location.protocol
            ) {
              target.protocol = window.location.protocol
              return { url: target.toString() }
            }
          } catch {
            // Not something we can parse; leave it exactly as it came.
          }
          return { url }
        },
      })

      // Add navigation controls
      map.current.addControl(new maplibregl.NavigationControl(), 'top-right')

      // Check geolocation permission status
      if ('permissions' in navigator) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          setLocationPermission(result.state)
        })
      }

      // Load pins after map is initialized
      map.current.on('load', () => {
        loadPinsFromBackend()

        if (map.current) {
          // Added before the cameras so the zone shading stays beneath them.
          alertZoneOverlayRef.current = new AlertZoneOverlay(map.current)
          alertZoneOverlayRef.current.setZones(alertZonesRef.current)

          cameraOverlayRef.current = new CameraOverlayManager(
            map.current,
            setIsLoadingCameras,
            (key) => alert(tRef.current(`map.${key}`))
          )
        }
      })
    }

    return () => {
      cameraOverlayRef.current?.destroy()
      cameraOverlayRef.current = null
      alertZoneOverlayRef.current?.destroy()
      alertZoneOverlayRef.current = null
    }
  }, [])

  // The map stays live without polling: the service worker forwards each push
  // to open tabs, and we also refresh whenever the tab regains focus. Both are
  // skipped mid-interaction so a refresh can't rip markers (or a drag) away.
  useEffect(() => {
    const refreshIfIdle = () => {
      if (!map.current || busyRef.current || draggingRef.current) return
      void loadPinsRef.current()
    }

    // A new marker someone else pushed, relayed by the service worker: refresh
    // so it appears. On a notification tap ('focus-pin'), also fly to it — that
    // is a deliberate user action, so it isn't gated on being idle.
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      if (!data || !map.current) return
      if (data.type === 'focus-pin') {
        if (typeof data.lng === 'number' && typeof data.lat === 'number') {
          map.current.flyTo({
            center: [data.lng, data.lat],
            zoom: 16,
            duration: 1200,
          })
        }
        void loadPinsRef.current()
      } else if (data.type === 'new-pin') {
        refreshIfIdle()
      }
    }

    // Catch up on anything that changed while the tab was hidden — this also
    // covers users who haven't enabled notifications (no push to relay).
    const onVisibility = () => {
      if (!document.hidden) refreshIfIdle()
    }

    const swSupported = 'serviceWorker' in navigator
    if (swSupported) {
      navigator.serviceWorker.addEventListener('message', onMessage)
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      if (swSupported) {
        navigator.serviceWorker.removeEventListener('message', onMessage)
      }
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  // Headings are stored as compass bearings, so the arrows have to be
  // counter-rotated whenever the map itself is rotated.
  useEffect(() => {
    if (!map.current) return

    const syncHeadingsToBearing = () => {
      const bearing = map.current?.getBearing() ?? 0
      pinPointsRef.current.forEach((pin) => {
        if (!isDirectional(pin.kind)) return
        applyHeadingTransforms(
          pin.marker.getElement(),
          pin.heading,
          bearing,
          pin.owned,
          pin.kind
        )
      })
    }

    map.current.on('rotate', syncHeadingsToBearing)
    return () => {
      map.current?.off('rotate', syncHeadingsToBearing)
    }
  }, [])

  // Handle map click events for pin placement
  useEffect(() => {
    if (!map.current) return

    const handleMapClick = (e: maplibregl.MapMouseEvent) => {
      if (isAddingPin) {
        setPendingPinLocation({ lng: e.lngLat.lng, lat: e.lngLat.lat })
        setShowDescriptionModal(true)
        setIsAddingPin(false)
      }
    }

    map.current.on('click', handleMapClick)
    cameraOverlayRef.current?.setPopupEnabled(!isAddingPin)

    return () => {
      if (map.current) {
        map.current.off('click', handleMapClick)
      }
    }
  }, [isAddingPin])

  const centerOnUserLocation = () => {
    if (!map.current) return

    setIsLocating(true)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { longitude, latitude } = position.coords

        // Center map on user location
        map.current.flyTo({
          center: [longitude, latitude],
          zoom: 16,
          duration: 1500,
        })

        // Remove existing marker if any
        if (userLocationMarker.current) {
          userLocationMarker.current.remove()
        }

        // Add user location marker
        userLocationMarker.current = new maplibregl.Marker({
          color: '#3b82f6',
        })
          .setLngLat([longitude, latitude])
          .addTo(map.current)

        setIsLocating(false)
        setLocationPermission('granted')
        setHasUserLocation(true)
      },
      (error) => {
        console.error('Error getting location:', error)
        setIsLocating(false)
        setLocationPermission('denied')
        alert(t('map.locationError'))
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 600000,
      }
    )
  }

  const handleDescriptionSubmit = async () => {
    // Prevent multiple submissions
    if (isSavingPin || isUpdatingPin) return

    if (isEditingPin && pinToEdit) {
      // Update existing pin description
      try {
        setIsUpdatingPin(true)
        const currentLang = language as 'en' | 'fr' | 'ar'

        await pinService.updatePin(
          pinToEdit,
          {
            description: pinDescription,
          },
          currentLang
        )

        setShowDescriptionModal(false)
        setIsEditingPin(false)
        setIsPinReadOnly(false)
        setPinToEdit(null)
        setPinDescription('')

        // Reload pins from backend to get translated versions
        await loadPinsFromBackend()
      } catch (error) {
        console.error('Failed to update pin description:', error)
        alert(
          t('map.errorUpdatingPin') || 'Failed to update pin. Please try again.'
        )
      } finally {
        setIsUpdatingPin(false)
      }
    } else if (pendingPinLocation) {
      // Add new pin
      await addPinPoint(
        pendingPinLocation.lng,
        pendingPinLocation.lat,
        pinDescription,
        pendingKind
      )
      setShowDescriptionModal(false)
      setPendingPinLocation(null)
      setPinDescription('')
    }
  }

  const handleDescriptionCancel = () => {
    setShowDescriptionModal(false)
    setPendingPinLocation(null)
    setPinDescription('')
    setIsEditingPin(false)
    setIsPinReadOnly(false)
    setPinToEdit(null)
  }

  // Deleting is reached from the description modal, which stays open behind
  // the confirmation so cancelling returns the user to editing.
  const handleDeleteRequest = () => {
    if (!pinToEdit) return
    setPinToDelete(pinToEdit)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (pinToDelete) {
      await removePinPoint(pinToDelete)
      setShowDeleteConfirm(false)
      setPinToDelete(null)
      // The pin is gone, so close the editor that was sitting behind this.
      setShowDescriptionModal(false)
      setIsEditingPin(false)
      setIsPinReadOnly(false)
      setPinToEdit(null)
      setPinDescription('')
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
    setPinToDelete(null)
  }

  const handleReloadPins = () => {
    loadPinsFromBackend(true)
  }

  const handleFetchCameras = () => {
    cameraOverlayRef.current?.fetchCameras()
  }

  // The + button opens the kind picker; picking a kind arms map placement.
  const handleToggleAddPin = () => {
    if (isAddingPin || showKindPicker) {
      setIsAddingPin(false)
      setShowKindPicker(false)
    } else {
      setShowKindPicker(true)
    }
  }

  const handlePickKind = (kind: PinKind) => {
    setPendingKind(kind)
    setShowKindPicker(false)
    setIsAddingPin(true)
  }

  // --- Alert zones ---
  // The rectangle is dragged out on a transparent surface laid over the map
  // rather than through MapLibre's own handlers, so the drag can't pan the map
  // underneath it.

  const persistAlertZones = (next: AlertZone[]) => {
    setAlertZones(next)
    // Fire and forget: the browser's own copy is the source of truth, and the
    // server gets the list again on the next subscribe.
    void saveAlertZones(next, language).catch((error) => {
      console.error('Failed to save alert zones:', error)
    })
  }

  const handleStartDrawingZone = () => {
    if (alertZones.length >= MAX_ALERT_ZONES) return
    // Placing a pin and drawing a zone both want the map surface.
    setIsAddingPin(false)
    setShowKindPicker(false)
    setZoneDraft(null)
    setIsDrawingZone(true)
  }

  const handleCancelDrawingZone = () => {
    setIsDrawingZone(false)
    setZoneDraft(null)
  }

  const handleRemoveZone = (id: string) => {
    persistAlertZones(alertZones.filter((zone) => zone.id !== id))
  }

  const handleFocusZone = (zone: AlertZone) => {
    map.current?.fitBounds(
      [
        [zone.west, zone.south],
        [zone.east, zone.north],
      ],
      { padding: 48, duration: 800 }
    )
  }

  const surfacePoint = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const handleZonePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const { x, y } = surfacePoint(e)
    e.currentTarget.setPointerCapture(e.pointerId)
    setZoneDraft({ x0: x, y0: y, x1: x, y1: y })
  }

  const handleZonePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!zoneDraft) return
    const { x, y } = surfacePoint(e)
    setZoneDraft((draft) => (draft ? { ...draft, x1: x, y1: y } : draft))
  }

  const handleZonePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const draft = zoneDraft
    setZoneDraft(null)
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    if (!draft || !map.current) return

    // Anything this small is a mis-tap, so stay armed and let them try again.
    const MIN_ZONE_PX = 16
    if (
      Math.abs(draft.x1 - draft.x0) < MIN_ZONE_PX ||
      Math.abs(draft.y1 - draft.y0) < MIN_ZONE_PX
    ) {
      return
    }

    // All four corners, because a rotated map turns a screen rectangle into a
    // rotated quad — the zone is then the north-aligned box around it.
    const lngs: number[] = []
    const lats: number[] = []
    for (const corner of [
      [draft.x0, draft.y0],
      [draft.x1, draft.y0],
      [draft.x1, draft.y1],
      [draft.x0, draft.y1],
    ] as [number, number][]) {
      const lngLat = map.current.unproject(corner).wrap()
      lngs.push(lngLat.lng)
      lats.push(lngLat.lat)
    }

    // Same rules the server applies, so nothing is stored that it would drop —
    // a box dragged across the antimeridian comes back inside-out, for one.
    const [zone] = sanitizeZones([
      {
        id: `zone-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        west: Math.min(...lngs),
        east: Math.max(...lngs),
        south: Math.min(...lats),
        north: Math.max(...lats),
      },
    ])
    if (!zone) return

    persistAlertZones([...alertZones, zone])
    setIsDrawingZone(false)
  }

  return (
    <div className={`relative ${className}`} style={{ height: '100dvh' }}>
      <div ref={mapContainer} className="w-full h-full" />

      {/* Drawing surface for a new alert zone. It sits over the map — and under
          the controls — so dragging draws a rectangle instead of panning. */}
      {isDrawingZone && (
        <div
          className="absolute inset-0 z-10 cursor-crosshair"
          style={{ touchAction: 'none' }}
          onPointerDown={handleZonePointerDown}
          onPointerMove={handleZonePointerMove}
          onPointerUp={handleZonePointerUp}
          onPointerCancel={handleZonePointerUp}
        >
          {zoneDraft && (
            <div
              className="absolute border-2 border-amber-500 bg-amber-400/20 pointer-events-none"
              style={{
                left: Math.min(zoneDraft.x0, zoneDraft.x1),
                top: Math.min(zoneDraft.y0, zoneDraft.y1),
                width: Math.abs(zoneDraft.x1 - zoneDraft.x0),
                height: Math.abs(zoneDraft.y1 - zoneDraft.y0),
              }}
            />
          )}
        </div>
      )}

      {/* Control Buttons */}
      <div
        className="absolute z-20 flex flex-col items-end gap-2"
        style={{
          bottom: 'max(env(safe-area-inset-bottom, 1rem), 1rem)',
          right: '1rem',
        }}
        // These are anchored to a physical corner, not to the text flow, so
        // they keep their layout in Arabic: `items-end` would otherwise align
        // them left and the kind picker would unfold on the wrong side.
        dir="ltr"
      >
        {/* Add Pin Button, with the kind picker unfolding to its left */}
        <div className="flex flex-row items-center gap-2">
          {showKindPicker && (
            <>
              <button
                onClick={() => handlePickKind('foot')}
                className="bg-[#1a365d] hover:bg-[#122744] text-white shadow-lg rounded-full p-4 transition-colors"
                aria-label={t('map.kindFoot')}
              >
                <PersonStanding className="w-6 h-6" />
              </button>
              <button
                onClick={() => handlePickKind('velo')}
                className="bg-[#1a365d] hover:bg-[#122744] text-white shadow-lg rounded-full p-4 transition-colors"
                aria-label={t('map.kindVelo')}
              >
                <Bike className="w-6 h-6" />
              </button>
              <button
                onClick={() => handlePickKind('car')}
                className="bg-[#1a365d] hover:bg-[#122744] text-white shadow-lg rounded-full p-4 transition-colors"
                aria-label={t('map.kindCar')}
              >
                <Car className="w-6 h-6" />
              </button>
              <button
                onClick={() => handlePickKind('horse')}
                className="bg-[#1a365d] hover:bg-[#122744] text-white shadow-lg rounded-full p-4 transition-colors"
                aria-label={t('map.kindHorse')}
              >
                <HorseIcon className="w-6 h-6" />
              </button>
            </>
          )}

          <button
            onClick={handleToggleAddPin}
            className={`shadow-lg rounded-full p-4 transition-colors ${
              isAddingPin || showKindPicker
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            aria-label={
              isAddingPin || showKindPicker
                ? t('map.cancelAddingPin')
                : t('map.addPinToMap')
            }
          >
            {isAddingPin || showKindPicker ? (
              <X className="w-6 h-6" />
            ) : (
              <Plus className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* GPS/Location Button */}
        <button
          onClick={centerOnUserLocation}
          disabled={isLocating}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white shadow-lg rounded-full p-4 transition-colors"
          aria-label={
            hasUserLocation ? t('map.centerOnLocation') : t('map.getLocation')
          }
        >
          {isLocating ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Navigation className="w-6 h-6" />
          )}
        </button>

        {/* Notification Toggle (hidden where push isn't supported) */}
        <NotificationToggle
          zones={alertZones}
          onDrawZone={handleStartDrawingZone}
          onRemoveZone={handleRemoveZone}
          onFocusZone={handleFocusZone}
        />

        {/* Reload Pins Button */}
        <button
          onClick={handleReloadPins}
          disabled={isReloading}
          className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white shadow-lg rounded-full p-4 transition-colors"
          aria-label="Reload pins from server"
        >
          {isReloading ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <RefreshCw className="w-6 h-6" />
          )}
        </button>

        {/* Surveillance Cameras Button */}
        <button
          onClick={handleFetchCameras}
          disabled={isLoadingCameras}
          className="bg-gray-700 hover:bg-gray-800 disabled:bg-gray-500 text-white shadow-lg rounded-full p-4 transition-colors"
          aria-label={t('map.showCameras')}
        >
          {isLoadingCameras ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Cctv className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Instructions for adding pins */}
      {(isAddingPin || showKindPicker) && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg z-20">
          {showKindPicker ? t('map.chooseKind') : t('map.clickToAddPin')}
        </div>
      )}

      {/* Instructions for drawing an alert zone, with the way out of it */}
      {isDrawingZone && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white ps-4 pe-2 py-2 rounded-lg z-20 flex items-center gap-2">
          <span>{t('zones.drawHint')}</span>
          <button
            onClick={handleCancelDrawingZone}
            className="p-1 rounded hover:bg-white/20 transition-colors"
            aria-label={t('common.cancel')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Pin Description Modal */}
      {showDescriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-40 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mt-8 mb-8 min-h-fit">
            <h3 className="text-lg font-semibold mb-4">
              {isPinReadOnly
                ? t('map.viewPinDescription')
                : isEditingPin
                  ? t('map.editPinDescription')
                  : t('map.addPinDescription')}
            </h3>
            {isPinReadOnly && (
              <p className="text-sm text-gray-500 mb-4">
                {t('map.notYourPin')}
              </p>
            )}
            {isEditingPin &&
              pinToEdit &&
              (() => {
                const currentPin = pinPointsRef.current.find(
                  (p) => p.id === pinToEdit
                )
                const creationTime = formatCreationTime(currentPin?.createdAt)
                return creationTime ? (
                  <p className="text-sm text-gray-500 mb-4">
                    {t('map.createdAt', 'Created')}: {creationTime}
                  </p>
                ) : null
              })()}
            <textarea
              value={pinDescription}
              onChange={(e) => setPinDescription(e.target.value)}
              placeholder={t('map.enterDescription')}
              disabled={isSavingPin || isUpdatingPin || isPinReadOnly}
              readOnly={isPinReadOnly}
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              rows={4}
              autoFocus={!isPinReadOnly}
            />
            <div className="flex gap-3 mt-4">
              {!isPinReadOnly && (
                <button
                  onClick={handleDescriptionSubmit}
                  disabled={isSavingPin || isUpdatingPin}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                >
                  {isSavingPin || isUpdatingPin ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('map.translating')}
                    </>
                  ) : isEditingPin ? (
                    t('map.updatePin')
                  ) : (
                    t('map.addPin')
                  )}
                </button>
              )}
              <button
                onClick={handleDescriptionCancel}
                disabled={isSavingPin || isUpdatingPin}
                className="flex-1 bg-gray-400 hover:bg-gray-500 disabled:bg-gray-300 text-white py-2 px-4 rounded-lg transition-colors"
              >
                {isPinReadOnly ? t('common.close') : t('common.cancel')}
              </button>
            </div>
            {isEditingPin && pinToEdit && !isPinReadOnly && (
              <button
                onClick={handleDeleteRequest}
                disabled={isSavingPin || isUpdatingPin}
                className="w-full mt-3 border border-red-600 text-red-600 hover:bg-red-50 disabled:opacity-50 py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {t('map.deletePin')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-40 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mt-16 mb-8 min-h-fit">
            <h3 className="text-lg font-semibold mb-4">
              {t('map.deleteConfirmTitle')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('map.deleteConfirmMessage')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                {t('common.delete')}
              </button>
              <button
                onClick={handleDeleteCancel}
                className="flex-1 bg-gray-400 hover:bg-gray-500 text-white py-2 px-4 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
